import { supabase } from "./supabase";

// O motor de conteúdo — leitura do aluno e escrita do admin (000073).
//
// Genérico por CARD de propósito: `ir-pro` é o primeiro morador, `mei-pro` entra
// sem migration de estrutura. Nada aqui sabe o que é IR Pro.
//
// As três tabelas NÃO são legíveis pela Data API — `revoke all` de `anon` e
// `authenticated`. Toda leitura sai por função que pergunta a `conteudo_liberado`,
// que por sua vez pergunta a `abrir_card`. Ou seja: quem decide se o aluno vê o
// conteúdo é a MESMA regra que decide se ele abre o card, e não uma segunda.

export type TipoDeBloco = "titulo" | "paragrafo" | "lista" | "imagem" | "video" | "destaque";
export type Tom = "info" | "atencao" | "positivo" | "neutro";

export type Bloco = {
  tipo: TipoDeBloco;
  texto: string | null;
  itens: string[] | null;
  ordenada: boolean | null;
  imagem_path: string | null;
  imagem_alt: string | null;
  video_id: string | null;
  tom: Tom | null;
};

export type BlocoEditavel = Bloco & { id: string; ordem: number };

export type PaginaNav = { slug: string; nome: string };
export type SecaoNav = { slug: string; nome: string; paginas: PaginaNav[] };

export type Navegacao = { permitido: boolean; secoes: SecaoNav[] };

export type Pagina = {
  permitido: boolean;
  existe: boolean;
  nome?: string;
  slug?: string;
  secao_nome?: string;
  secao_slug?: string;
  blocos?: Bloco[];
};

export type Escrita<T> = { ok: true; res: T } | { ok: false; mensagem: string };

function falha(mensagem: string | undefined): { ok: false; mensagem: string } {
  return {
    ok: false,
    mensagem: mensagem?.trim()
      ? mensagem
      : "Não foi possível concluir agora. Tente de novo em instantes.",
  };
}

// ---------------------------------------------------------------------------
// O que o aluno lê
// ---------------------------------------------------------------------------
export async function carregarNavegacao(card: string): Promise<Navegacao> {
  const { data, error } = await supabase.rpc("conteudo_navegacao", { p_card: card });
  if (error || !data) return { permitido: false, secoes: [] };
  return data as Navegacao;
}

export async function carregarPagina(
  card: string,
  secao: string,
  pagina: string,
): Promise<Pagina> {
  const { data, error } = await supabase.rpc("conteudo_pagina", {
    p_card: card,
    p_secao: secao,
    p_pagina: pagina,
  });
  if (error || !data) return { permitido: false, existe: false };
  return data as Pagina;
}

/**
 * O endereço público da imagem no Storage.
 *
 * O bucket `conteudo` é PÚBLICO, e a razão está na 000073: `<img src>` não carrega
 * cabeçalho de autorização, então bucket privado exigiria URL assinada — que
 * expira, e expirar quebra a página que o aluno deixou aberta. O que fica
 * protegido é o texto, que é o produto.
 */
export function urlDaImagem(caminho: string): string {
  return supabase.storage.from("conteudo").getPublicUrl(caminho).data.publicUrl;
}

// ---------------------------------------------------------------------------
// O que o admin edita
// ---------------------------------------------------------------------------
export type SecaoAdmin = {
  id: string;
  slug: string;
  nome: string;
  ordem: number;
  publicada: boolean;
  paginas: PaginaAdmin[];
};

export type PaginaAdmin = {
  id: string;
  slug: string;
  nome: string;
  ordem: number;
  publicada: boolean;
  blocos: number;
};

export async function carregarArvoreAdmin(card: string): Promise<SecaoAdmin[]> {
  const { data, error } = await supabase.rpc("conteudo_admin_arvore", { p_card: card });
  if (error || !data) return [];
  return data as SecaoAdmin[];
}

export async function carregarBlocosAdmin(pagina: string): Promise<BlocoEditavel[]> {
  const { data, error } = await supabase.rpc("conteudo_admin_blocos", { p_pagina: pagina });
  if (error || !data) return [];
  return data as BlocoEditavel[];
}

export async function salvarSecao(campos: {
  id?: string | null;
  card?: string;
  nome?: string;
  slug?: string;
  publicada?: boolean;
}): Promise<Escrita<{ id: string }>> {
  const { data, error } = await supabase.rpc("conteudo_salvar_secao", {
    p_id: campos.id ?? null,
    p_card: campos.card ?? null,
    p_nome: campos.nome ?? null,
    p_slug: campos.slug ?? null,
    p_publicada: campos.publicada ?? null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as { id: string } };
}

export async function salvarPagina(campos: {
  id?: string | null;
  secao?: string;
  nome?: string;
  slug?: string;
  publicada?: boolean;
}): Promise<Escrita<{ id: string }>> {
  const { data, error } = await supabase.rpc("conteudo_salvar_pagina", {
    p_id: campos.id ?? null,
    p_secao: campos.secao ?? null,
    p_nome: campos.nome ?? null,
    p_slug: campos.slug ?? null,
    p_publicada: campos.publicada ?? null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as { id: string } };
}

/**
 * Salva o bloco INTEIRO, sempre — e `null` quer dizer "este tipo não usa".
 *
 * Diferente da convenção da 000042 ("nulo = não mexe"), e o motivo é o check
 * `conteudo_blocos_coerente`: trocar um bloco de imagem para parágrafo tem de
 * LIMPAR o `imagem_path`, senão a constraint recusa com uma mensagem que não
 * explica nada a quem está editando.
 */
export async function salvarBloco(campos: {
  id?: string | null;
  pagina?: string;
  tipo: TipoDeBloco;
  texto?: string | null;
  itens?: string[] | null;
  ordenada?: boolean | null;
  imagem_path?: string | null;
  imagem_alt?: string | null;
  video_id?: string | null;
  tom?: Tom | null;
}): Promise<Escrita<BlocoEditavel>> {
  const { data, error } = await supabase.rpc("conteudo_salvar_bloco", {
    p_id: campos.id ?? null,
    p_pagina: campos.pagina ?? null,
    p_tipo: campos.tipo,
    p_texto: campos.texto ?? null,
    p_itens: campos.itens ?? null,
    p_ordenada: campos.ordenada ?? null,
    p_imagem_path: campos.imagem_path ?? null,
    p_imagem_alt: campos.imagem_alt ?? null,
    p_video_id: campos.video_id ?? null,
    p_tom: campos.tom ?? null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: (Array.isArray(data) ? data[0] : data) as BlocoEditavel };
}

/** A lista COMPLETA de ids na ordem nova — a RPC recusa lista incompleta. */
export async function reordenar(
  o_que: "secoes" | "paginas" | "blocos",
  pai: string,
  ids: string[],
): Promise<Escrita<null>> {
  const nome = `conteudo_reordenar_${o_que}`;
  const chave = o_que === "secoes" ? "p_card" : o_que === "paginas" ? "p_secao" : "p_pagina";
  const { error } = await supabase.rpc(nome, { [chave]: pai, p_ids: ids });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}

export async function excluir(
  o_que: "secao" | "pagina" | "bloco",
  id: string,
): Promise<Escrita<null>> {
  const { error } = await supabase.rpc(`conteudo_excluir_${o_que}`, { p_id: id });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}

/**
 * Sobe uma imagem para o bucket `conteudo`.
 *
 * O caminho leva um id aleatório e NÃO o nome do arquivo: nome de arquivo vindo do
 * navegador de alguém é texto de fora, e um `../` ali dentro é travessia de
 * caminho. Só a extensão sobrevive, e só se estiver na lista.
 */
export async function subirImagem(card: string, arquivo: File): Promise<Escrita<string>> {
  const ext = (arquivo.name.match(/\.(png|jpe?g|webp|gif)$/i)?.[1] ?? "").toLowerCase();
  if (ext === "") {
    return { ok: false, mensagem: "A imagem precisa ser .png, .jpg, .webp ou .gif." };
  }
  const caminho = `${card}/${crypto.randomUUID()}.${ext === "jpg" ? "jpeg" : ext}`;
  const { error } = await supabase.storage.from("conteudo").upload(caminho, arquivo, {
    contentType: arquivo.type,
    upsert: false,
  });
  if (error) return falha(error.message);
  return { ok: true, res: caminho };
}
